import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
    AccountsCell,
    AccountsColumn,
    AccountsRow,
    AccountStatus,
    ColumnKind,
    FilterGroup,
} from './accounts-overview.types';

const STATUS_VARIANTS: Record<AccountStatus, 'green' | 'red' | 'gray'> = {
    reconciled: 'green',
    difference: 'red',
    open: 'gray',
};

/**
 * Fixed column widths, so opening a position (account numbers, status badges) never moves a column.
 * The label column takes the rest, but never less than its floor: below that the table scrolls in its
 * frame instead of cutting every name short.
 */
const NUMBER_COLUMN_WIDTH = 96;
const LABEL_COLUMN_MIN_WIDTH = 264;
const STATUS_COLUMN_WIDTH = 136;
const VALUE_COLUMN_WIDTHS: Record<ColumnKind, number> = {
    saldo: 144,
    deltaAbsolute: 144,
    percent: 96,
    deltaPercent: 96,
};

const STATUS_LABEL_KEYS: Record<AccountStatus, string> = {
    reconciled: 'abcp_accounts_status_reconciled',
    difference: 'abcp_accounts_status_difference',
    open: 'abcp_accounts_status_open',
};

@Component({
    selector: 'abcp-accounts-overview',
    templateUrl: './accounts-overview.component.html',
    styleUrls: ['./accounts-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAccountsOverviewComponent {
    @Input() columns: AccountsColumn[] = [];
    @Input() rows: AccountsRow[] = [];
    @Input() filterGroups: FilterGroup[] = [];
    @Input() showStatus = false;
    /** Shows the review flag on marked rows. `false` hides it while review is not offered. */
    @Input() showReview = true;
    @Input() loading = false;
    @Input() error = false;

    @Output() groupToggle = new EventEmitter<string>();
    @Output() accountSelect = new EventEmitter<string>();
    @Output() filterToggle = new EventEmitter<string>();
    /** «Details anzeigen» (true) or «Details ausblenden» (false): every position at once. */
    @Output() detailsToggle = new EventEmitter<boolean>();

    readonly skeletonRows = [1, 2, 3, 4, 5, 6];
    readonly numberColumnWidth = NUMBER_COLUMN_WIDTH;
    readonly statusColumnWidth = STATUS_COLUMN_WIDTH;

    /** A statement in sections carries its column headings in the section rows. */
    get hasSections(): boolean {
        return this.rows.some((row) => row.kind === 'section');
    }

    get hasPositions(): boolean {
        return this.rows.some((row) => row.kind === 'group');
    }

    /** Every position open: the button then offers to close them. */
    get allExpanded(): boolean {
        return this.rows.every((row) => row.kind !== 'group' || row.isExpanded);
    }

    get isEmpty(): boolean {
        return this.rows.length === 0;
    }

    /**
     * Placeholders only while there is nothing to show yet. A reload keeps the rows it has until the new
     * ones arrive: placeholders over old rows read as two states at once, and swapping the rows for
     * placeholders on every refresh (a poll, a reload after a write) makes the table flash.
     */
    get showSkeleton(): boolean {
        return this.loading && this.rows.length === 0;
    }

    get columnCount(): number {
        return 2 + this.columns.length + (this.showStatus ? 1 : 0);
    }

    get tableMinWidth(): number {
        return (
            NUMBER_COLUMN_WIDTH +
            LABEL_COLUMN_MIN_WIDTH +
            this.columns.reduce((sum, column) => sum + VALUE_COLUMN_WIDTHS[column.kind], 0) +
            (this.showStatus ? STATUS_COLUMN_WIDTH : 0)
        );
    }

    columnWidth(column: AccountsColumn): number {
        return VALUE_COLUMN_WIDTHS[column.kind];
    }

    cellAt(values: AccountsCell[] | undefined, index: number): AccountsCell | null {
        return values?.[index] ?? null;
    }

    /** The rule above a total is the bottom border of the row before it, so two totals share one line. */
    isBeforeTotal(index: number): boolean {
        const next = this.rows[index + 1]?.kind;
        return next === 'total' || next === 'subtotal';
    }

    trackByRowId(_index: number, row: AccountsRow): string {
        return row.id;
    }

    trackById(_index: number, item: { id: string }): string {
        return item.id;
    }

    numberFormat(column?: AccountsColumn): string {
        return this.isPercent(column) ? '1.1-1' : '1.2-2';
    }

    signPrefix(value: number, column?: AccountsColumn): string {
        const isDelta = column?.kind === 'deltaAbsolute' || column?.kind === 'deltaPercent';
        return isDelta && value >= 0 ? '+' : '';
    }

    unitSuffix(column?: AccountsColumn): string {
        return this.isPercent(column) ? '%' : '';
    }

    statusLabelKey(status: AccountStatus): string {
        return STATUS_LABEL_KEYS[status];
    }

    statusVariant(status: AccountStatus): 'green' | 'red' | 'gray' {
        return STATUS_VARIANTS[status];
    }

    onGroupClick(id: string): void {
        this.groupToggle.emit(id);
    }

    onAccountClick(id: string): void {
        this.accountSelect.emit(id);
    }

    onFilterClick(id: string): void {
        this.filterToggle.emit(id);
    }

    onDetailsClick(): void {
        this.detailsToggle.emit(!this.allExpanded);
    }

    private isPercent(column?: AccountsColumn): boolean {
        return column?.kind === 'percent' || column?.kind === 'deltaPercent';
    }
}
